import { ChevronLeft, ChevronRight } from 'lucide-react'

interface PaginationProps {
  /** 1-based page index (MBA convention). */
  page: number
  limit: number
  total: number
  onPageChange: (page: number) => void
  /** Noun for the meta line, e.g. `customers` when paginating whole groups. */
  unitLabel?: string
  /**
   * When set (packed customer pages), page controls use this instead of
   * ``ceil(total / limit)``. Meta range still uses total/unitLabel with
   * optional rangeStart/rangeEnd.
   */
  totalPages?: number
  rangeStart?: number
  rangeEnd?: number
}

/** Numbered pagination — ported from mba-app `components/pagination.tsx`. */
export function Pagination({
  page,
  limit,
  total,
  onPageChange,
  unitLabel = 'results',
  totalPages: totalPagesProp,
  rangeStart,
  rangeEnd,
}: PaginationProps) {
  const totalPages = Math.max(1, totalPagesProp ?? Math.ceil(total / Math.max(limit, 1)))

  if (total === 0) {
    return (
      <div className="pagination">
        <span className="pagination__meta">Showing 0 {unitLabel}</span>
      </div>
    )
  }

  const start = rangeStart ?? (page - 1) * limit + 1
  const end = rangeEnd ?? Math.min(page * limit, total)

  if (totalPages <= 1) {
    return (
      <div className="pagination">
        <span className="pagination__meta">
          Showing {total} {unitLabel}
        </span>
      </div>
    )
  }

  const pageNums: number[] = []
  const windowSize = Math.min(5, totalPages)
  for (let i = 0; i < windowSize; i += 1) {
    if (totalPages <= 5) pageNums.push(i + 1)
    else if (page <= 3) pageNums.push(i + 1)
    else if (page >= totalPages - 2) pageNums.push(totalPages - 4 + i)
    else pageNums.push(page - 2 + i)
  }

  return (
    <div className="pagination">
      <span className="pagination__meta">
        Showing {start}–{end} of {total} {unitLabel}
      </span>
      <div className="pagination__controls">
        <button
          type="button"
          className="pagination__btn"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          <ChevronLeft size={16} strokeWidth={2} aria-hidden />
        </button>
        {pageNums.map((pageNum) => (
          <button
            key={pageNum}
            type="button"
            className={`pagination__btn pagination__btn--page${pageNum === page ? ' pagination__btn--active' : ''}`}
            onClick={() => onPageChange(pageNum)}
            aria-label={`Page ${pageNum}`}
            aria-current={pageNum === page ? 'page' : undefined}
          >
            {pageNum}
          </button>
        ))}
        <button
          type="button"
          className="pagination__btn"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          aria-label="Next page"
        >
          <ChevronRight size={16} strokeWidth={2} aria-hidden />
        </button>
      </div>
    </div>
  )
}
